'use client';

import { useCallback, useId, useRef, useState } from 'react';
import { format } from 'date-fns';
import { MdArrowDropDown } from 'react-icons/md';
import { useTranslations } from 'next-intl';
import { BaseInput, FieldWrapper } from '@/components/ui/input';
import { RangeCalendarPanel } from './RangeCalendarPanel';
import type { DateRange, DateRangePickerProps } from './DateRangePicker.types';

/**
 * Normalize a range so the start is never after the end.
 *
 * When both bounds are present and inverted, swap them. Guarantees the picker
 * never displays or emits a range with Start > End regardless of what a consumer
 * feeds in as a controlled `value` (PB-3551).
 */
function normalizeRange(range: DateRange): DateRange {
  if (range.start && range.end && range.start > range.end) {
    return { start: range.end, end: range.start };
  }
  return range;
}

/**
 * DateRangePicker — select a start and end date range.
 *
 * Uses the same FieldWrapper + BaseInput + popup pattern as DatePicker.
 * Displays the selected range as "01/15/2026 – 01/22/2026".
 *
 * ## Invariants:
 * - The displayed and emitted range always satisfies `start <= end`. An inverted
 *   controlled `value` is normalized (swapped) rather than rendered/emitted
 *   backwards, so a consumer can never surface a "07/30 – 07/23" range (PB-3551).
 */
export function DateRangePicker({
  value: controlledValue,
  onChange,
  defaultValue,
  dateFormat = 'MM/dd/yyyy',
  placeholder,
  startPlaceholder,
  endPlaceholder,
  label,
  required,
  requiredMessage,
  error,
  helperText,
  disabled,
  clearable = false,
  minDate,
  maxDate,
  filterDate,
  className = '',
  inputClassName = '',
  id,
  leftIcon,
  hideBottomPadding,
  'data-cy': dataCy,
  menuZIndex,
}: DateRangePickerProps) {
  const t = useTranslations('DatePicker');
  const generatedId = useId();
  const inputId = id ?? `daterangepicker-input-${generatedId}`;
  const helperId = `${inputId}-helper`;

  const emptyRange: DateRange = { start: null, end: null };

  const [uncontrolledValue, setUncontrolledValue] = useState<DateRange>(
    () => defaultValue ?? emptyRange
  );
  const [open, setOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);

  const anchorRef = useRef<HTMLDivElement>(null);
  const baseInputContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipNextOpenRef = useRef(false);

  const resolvedStartPlaceholder = startPlaceholder ?? t('startDate');
  const resolvedEndPlaceholder = endPlaceholder ?? t('endDate');
  const isControlled = controlledValue !== undefined;
  const range = normalizeRange(isControlled ? controlledValue : uncontrolledValue);

  // Build display value
  const formatDisplay = (): string => {
    if (range.start && range.end) {
      return `${format(range.start, dateFormat)} – ${format(range.end, dateFormat)}`;
    }
    if (range.start) {
      return `${format(range.start, dateFormat)} – ${resolvedEndPlaceholder}`;
    }
    return '';
  };

  const displayValue = formatDisplay();
  const displayPlaceholder = placeholder ?? `${resolvedStartPlaceholder} – ${resolvedEndPlaceholder}`;

  const handleValidationChange = useCallback((error: string | null) => {
    setValidationError(error);
  }, []);

  const hasSelection = range.start != null || range.end != null;

  const displayError =
    open && hasSelection
      ? null
      : typeof error === 'string'
        ? error
        : error === true
          ? ''
          : validationError;

  const handleOpen = useCallback(() => {
    if (disabled || open) return;
    if (skipNextOpenRef.current) {
      skipNextOpenRef.current = false;
      return;
    }
    setOpen(true);
    setOpenCount((prev) => prev + 1);
  }, [disabled, open]);

  const handleClose = useCallback(() => {
    setOpen(false);
    skipNextOpenRef.current = true;
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  const handleSelect = useCallback(
    (newRange: DateRange) => {
      const normalized = normalizeRange(newRange);
      if (!isControlled) setUncontrolledValue(normalized);
      onChange?.(normalized);
    },
    [isControlled, onChange]
  );

  const handleClear = useCallback(() => {
    skipNextOpenRef.current = true;
    const cleared = { start: null, end: null };
    if (!isControlled) {
      setUncontrolledValue(cleared);
    }
    onChange?.(cleared);
    setOpen(false);
  }, [isControlled, onChange]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        handleOpen();
      }
    },
    [disabled, handleOpen]
  );

  const displayTitle = label ?? t('selectDateRange');

  return (
    <div
      ref={anchorRef}
      className={`tw:relative tw:min-w-0 tw:w-full ${className}`.trim()}
      onClick={disabled ? undefined : handleOpen}
    >
      <FieldWrapper
        label={label}
        required={required}
        error={displayError}
        helperText={helperText}
        id={inputId}
        helperId={helperId}
        hideBottomPadding={hideBottomPadding}
      >
        <BaseInput
          ref={inputRef}
          id={inputId}
          data-cy={dataCy}
          readOnly
          value={displayValue}
          placeholder={displayPlaceholder}
          required={open && hasSelection ? false : required}
          requiredMessage={requiredMessage}
          error={error}
          helperText={helperText}
          disabled={disabled}
          leftIcon={leftIcon}
          rightIcon={
            <MdArrowDropDown
              data-cy="date-range-picker-trigger"
              className="tw:h-5 tw:w-5 tw:text-gray-darker"
              aria-hidden="true"
            />
          }
          onKeyDown={handleInputKeyDown}
          onClear={clearable && hasSelection && !disabled ? handleClear : undefined}
          clearAriaLabel={clearable && hasSelection && !disabled ? t('clearDateRange') : undefined}
          onValidationChange={handleValidationChange}
          inputClassName={`tw:cursor-pointer ${inputClassName}`.trim()}
          containerRef={baseInputContainerRef}
          // PB-5609: see DatePicker — aria-expanded is not allowed on a textbox, so it is
          // dropped rather than left asserting a state no AT is obliged to read.
          aria-haspopup="dialog"
        />
      </FieldWrapper>
      <RangeCalendarPanel
        key={openCount}
        open={open}
        onClose={handleClose}
        onSelect={handleSelect}
        selectedRange={range}
        minDate={minDate}
        maxDate={maxDate}
        filterDate={filterDate}
        anchorRef={baseInputContainerRef}
        title={displayTitle}
        menuZIndex={menuZIndex}
      />
    </div>
  );
}
